import type { FrequentCustomerCard } from '@washday/utilities';
import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';
import { createCustomerV2, deleteCustomerByIdV2, updateCustomerByIdV2 } from '../../services/customer.service';
const listQueryKey = 'customers';

export const useDeleteCustomerById = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const res = await deleteCustomerByIdV2(id);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    }
  });
};

export const useCreateCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      frequentCustomerCard?: FrequentCustomerCard | null;
      lastName?: string;
      phone?: string;
      email?: string;
      address?: any;
      notes?: string;
      privateNotes?: string;
      discount: number;
      credit: number;
      rfc?: string;
      invoiceInfo?: {
        legal_name?: string;
        tax_system?: string;
        zipCode?: string;
        applyIsrRetention?: boolean;
      };
      isActive: boolean;
      createdIn: string;
    }) => {
      try {
        if (!data.email) {
          delete data.email;
        } else if (data.email && data.email.trim() === '') {
          delete data.email;
        } else {
          data.email = data.email ? data.email.trim().toLowerCase() : '';
        }
        const res = await createCustomerV2(data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    }
  });
};

export const useUpdateCustomerById = (id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      name?: string;
      frequentCustomerCard?: FrequentCustomerCard | null;
      phone?: string;
      email?: string;
      address?: any;
      notes?: string;
      privateNotes?: string;
      discount?: number;
      credit?: number;
      rfc?: string;
      invoiceInfo?: {
        legal_name?: string;
        tax_system?: string;
        zipCode?: string;
        applyIsrRetention?: boolean;
      };
      isActive?: boolean;
      createdIn?: string;
    }) => {
      try {
        if (!data.email) {
          delete data.email;
        } else if (data.email && data.email.trim() === '') {
          delete data.email;
        } else {
          data.email = data.email ? data.email.trim().toLowerCase() : '';
        }
        const res = await updateCustomerByIdV2(id, data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while updating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      //INVALIDATE CACHED  WITH STORE ID AND ID
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      }), queryClient.invalidateQueries({
        queryKey: [listQueryKey, { _id: id }],
      })]);
    }
  });
};
